
import { computed, defineComponent, h, PropType, ref, watchEffect, watch } from 'vue';
import RaceInfo from '../RaceInfo.vue';
import Alert from '../Alert.vue';
import { Database, Search, ArrowUp, ArrowDown } from '@vicons/fa';
import { NFormItem, NIcon, NInput, NText, NSpace } from 'naive-ui';
import Timer from '../Timer.vue';
import { HoundModel, QueueModel, RaceModel, formatNumberDecimals, weatherInventory, surfaceInventory, distanceInventory } from '../../../../common';
import { commitedFilters, filtersModel, generateFilterUI, generateSelectMenu, refresh } from './race-filters';
import { default as dayjs } from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';
import { default as duration } from 'dayjs/plugin/duration';
import { EthersService as eth, arenaOptions, participantCountOptions } from '../../services';
import breakpoints from '../../utils/breakpoints';

dayjs.extend(utc);
dayjs.extend(duration);

export default defineComponent({
  emits: ['search', 'join', 'unenqueue'],
  components: {
    Alert,
    'icon-no-races': Database,
    'icon-search': Search
  },
  name: 'RacesTable',
  props: {
    races: {
      type: Array as PropType<QueueModel[]|RaceModel[]>,
      required: true
    },
    raceType: {
      type: String,
      required: true
    }
  },
  setup (props, { emit }) {
    const rerenderKey = ref(0);
    const races = ref<RaceModel[]>([]);
    const queues = ref<QueueModel[]>([])
    const raceType = props.raceType;
    watchEffect(() => {
      races.value = [];
      queues.value = [];
      if (raceType === 'live') {
        (props.races as RaceModel[]).forEach(e => {
          races.value.push(new RaceModel(e));
        });
      } else {
        (props.races as QueueModel[]).forEach(e => {
          queues.value.push(new QueueModel(e));
        });
      }
    });
    const data = computed(() => {
      if (raceType === 'live') {
        return races.value.map(el => ({ ...el, key: el.id }));  
      } else {
        return queues.value.map(el => ({ ...el, key: el.id }));  
      }
    });
    const search = () => {
      emit('search');
    };

    watch(() => refresh.value, (_) => {
      if (refresh.value) search();
      refresh.value = false;
    });

    const updateSort = (ev) => {
      if (ev.columnKey === 'ticketPrice') {
        if (ev.order === false) filtersModel.ticketOrder = null;
        else if (ev.order === 'ascend') filtersModel.ticketOrder = 'ASC';
        else if (ev.order === 'descend') filtersModel.ticketOrder = 'DESC';
        search();
      }
    };

    const updateExpandedRowKeys = (ev) => {
      expandedRowKeys.value = ev;
    }

    const expandedRowKeys = ref<number[]>([]);
    const pauseAnimations = ref(false);

    const rowClick = (rowData: QueueModel|RaceModel) => {
      const id = rowData.id;
      if (expandedRowKeys.value.includes(id)) expandedRowKeys.value.splice(expandedRowKeys.value.findIndex(el => el === id), 1);
      else expandedRowKeys.value.push(id);
    };

    return {
      rowClick,
      columns: [
        {
          type: 'expand',
          renderExpand: (rowData: QueueModel|RaceModel) => {
            return h(
              RaceInfo,
              {
                race: rowData,
                raceType: raceType,
                pauseAnimations: pauseAnimations.value,
                onJoin: () => {
                  emit('join');
                },
                onUnenqueue: () => {
                  emit('unenqueue');
                }
              }
            );
          },
          fixed: breakpoints.sDown.matches ? null : 'left'
        },
        {
          title: () => {
            return generateFilterUI('Title', !!commitedFilters.value.title, 'title', search, [
              h(NFormItem, { label: 'Title', showFeedback: false },
                {
                  default: () => (h(
                    NInput,
                    {
                      style: { width: 'auto' }, placeholder: 'Title', value: filtersModel.title,
                      onInput: (newTitle: string) => {
                        filtersModel.title = newTitle;
                      }
                    }
                  ))
                }
              )
            ]);
          },
          key: 'title',
          align: 'center',
          render(row: QueueModel) {
            return row.name || 'N/A';
          },
        },
        {
          title: () => generateFilterUI('Arena', !!commitedFilters.value.arena, 'arena', search, [generateSelectMenu('Arena', 'arena', arenaOptions.value.map(a => ({ label: a.label, value: a.value })))]),
          key: 'arena',
          align: 'center',
          render (row: QueueModel) {
            return row.arena.name || 'N/A';
          }
        },
        {
          title: () => generateFilterUI('Surface', (commitedFilters.value.surfaceType !== null), 'surfaceType', search, [generateSelectMenu('Surface', 'surfaceType', surfaceInventory)]),
          key: 'surface',
          align: 'center',
          render (row: QueueModel) {
            return row.arena.surfaceType() || 'N/A';
          },
        },
        {
          title: () => generateFilterUI('Distance', (commitedFilters.value.distanceType !== null), 'distanceType', search, [generateSelectMenu('Distance', 'distanceType', distanceInventory)]),
          key: 'distance',
          align: 'center',
          render (row: QueueModel) {
            return row.arena.distanceType() || 'N/A';
          }
        },
        {
          title: () => generateFilterUI('Weather', (commitedFilters.value.weatherType !== null), 'weatherType', search, [generateSelectMenu('Weather', 'weatherType', weatherInventory)]),
          key: 'weather',
          align: 'center',
          render (row: QueueModel) {
            return row.arena.weatherType() || 'N/A';
          }
        },
        {
          title: 'Token',
          key: 'currency',
          align: 'center',
          render (row: QueueModel) {
            return eth.currenciesInfo.find(c => c.value === row.entryFeeCurrency)?.label;
          }
        },
        {
          title: 'Ticket',
          key: 'ticketPrice',
          align: 'center',
          // sorter: true,
          // renderSorter: () => {
          //   let icon;
          //   if (commitedFilters.value.ticketOrder === 'ASC')
          //     icon = h(NIcon, { style: { marginLeft: '.5rem' } }, { default: () => h(ArrowUp) });
          //   else if (commitedFilters.value.ticketOrder === 'DESC')
          //     icon = h(NIcon, { style: { marginLeft: '.5rem' } }, { default: () => h(ArrowDown) });
          //   else icon = h(NIcon, { style: { marginLeft: '.5rem' }, depth: 5 }, { default: () => h(ArrowDown) });

          //   return h(NText, { type: 'info' }, { default: () => icon });
          // },
          render (row: QueueModel) {
            return h(
              NSpace,
              { justify: 'center', vertical: true, size: [1, 1] },
              {
                default: () => row.entryFee !== '0'
                  ? h('div', null, [h(NText, { style: 'margin-right: .25rem;' }, () => eth.currenciesInfo.find(c => c.value === row.entryFeeCurrency)?.label), formatNumberDecimals(eth.utils.formatEther(row.entryFee))])
                  : h(NText, { type: 'success' }, () => 'Free')
              }
            );
          }
        },
        {
          title: 'Pool',
          key: 'pool',
          align: 'center',
          render (row: QueueModel) {
            return h(
              NSpace,
              { justify: 'center', vertical: true, size: [1, 1] },
              {
                default: () => row.entryFee !== '0'
                  ? h('div', null, [h(NText, { style: 'margin-right: .25rem;' }, () => eth.currenciesInfo.find(c => c.value === row.entryFeeCurrency)?.label), formatNumberDecimals(Number(eth.utils.formatEther(row.entryFee)) * (row.totalParticipants || row.participants.length))])
                  : '-'
              }
            );
          }
        },
        {
          title: () => generateFilterUI('Registered', !!commitedFilters.value.totalParticipants, 'totalParticipants', search, [generateSelectMenu('Registered', 'totalParticipants', participantCountOptions.value.map(pc => ({ label: pc.label, value: pc.value })))]),
          key: 'registered',
          align: 'center',
          render (row: QueueModel) {
            return `${row.participants.filter((el: HoundModel) => el !== null).length}/${row.totalParticipants || row.participants.length}`;
          },
          width: 125,
          fixed: breakpoints.sDown.matches ? null : 'right'
        },
        {
          title: 'State',
          key: 'start',
          align: 'center',
          render (row: RaceModel) {
            switch (raceType) {
              case 'join':
                return h(NText, null, () => 'Filling');
              case 'live':
                const currentDate = dayjs().utc();
                const actualStartDate = dayjs(row.createdAt).utc().add(2, 'minute');
                const minutesRemaining =  actualStartDate.diff(currentDate)/(1000 * 60);
                const raceTime = parseInt(row.arena.distanceType().split('m')[0]) / 100 * 3;
                const actualRaceEndDate = actualStartDate.add(raceTime, 'second');

                if (currentDate.isBefore(actualStartDate)) {
                  return h(Timer, {
                    minutes: minutesRemaining,
                    onFinished: () => {
                      pauseAnimations.value = true;
                      rerenderKey.value++;
                      setTimeout(() => {
                        pauseAnimations.value = false;
                      }, 10);
                    }
                  });
                } else if (currentDate.isBefore(actualRaceEndDate)) {
                  const msRemaining = actualRaceEndDate.diff(currentDate);
                  setTimeout(() => {
                    pauseAnimations.value = true;
                    rerenderKey.value++;
                    setTimeout(() => {
                      pauseAnimations.value = false;
                    }, 10);
                  }, msRemaining);
                  return h(NText, null, () => 'Live');
                } else {
                  return h(NText, null, () => 'Ended');
                }
            }
          },
          width: 125,
          fixed: breakpoints.sDown.matches ? null : 'right'
        }
      ],
      data,
      raceType,
      search,
      updateSort,
      rerenderKey,
      updateExpandedRowKeys,
      expandedRowKeys,
      breakpoints
    };
  }
});
